import React, {forwardRef} from 'react';
import {TableFunctions} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/Table.type';
import {BaselineItem, RoleRow, EyeOptionMap, Company} from './BaselineTypes';
import {useIntl} from 'react-intl';
import {Table} from '@forecast-it/design-system';
import Util from '../../forecast-app/shared/util/util';
import {OnSaveError, OnSaveErrorType} from './Util';

type RoleTableProps = {
	editMode?: boolean;
	onDeleteRow: (row: RoleRow) => void;
	onRoleSelect: (roleId: string, row: RoleRow) => void;
	company: Company;
	currency: string;
	ref: React.MutableRefObject<TableFunctions>;
	recalculate?: (row: RoleRow) => void;
	setTableData?: React.Dispatch<React.SetStateAction<BaselineItem[]>>;
	tableData: RoleRow[];
	eyeOptionMap?: EyeOptionMap;
	onSaveError?: OnSaveError;
	disabledRoleIds?: string[];
};

export const TimeEstimatesTable = forwardRef<TableFunctions, RoleTableProps>((props, ref) => {
	const {
		editMode,
		onDeleteRow,
		company,
		currency,
		onRoleSelect,
		recalculate,
		tableData,
		setTableData,
		eyeOptionMap,
		onSaveError,
		disabledRoleIds,
	} = props;

	const {formatMessage} = useIntl();

	const getRoleName = (row: RoleRow) => {
		const role = company?.roles?.edges?.find(edge => edge?.node?.id === row.roleId);
		if (role && role.node && role.node.name) {
			return role.node.name;
		}
		return formatMessage({id: 'deliverable.select_role'});
	};

	const sortedRoles = React.useMemo(() => {
		const edges = company?.roles?.edges || [];
		const eligibleEdges = disabledRoleIds?.length
			? edges.filter(edge => !edge?.node?.id || !disabledRoleIds.includes(edge.node.id))
			: edges;
		return [...eligibleEdges].sort((a, b) => Util.sortAlphabetically(a?.node?.name, b?.node?.name));
	}, [company?.roles?.edges, disabledRoleIds]);

	const getDropdownState = (row: RoleRow) => {
		const highlightAsInputError = onSaveError && onSaveError.type === OnSaveErrorType.MISSING_ROLE && !row.roleId;
		return highlightAsInputError ? 'error' : 'default';
	};

	return (
		<>
			<Table width={'100%'} tableRef={ref} setTableData={setTableData} data={tableData}>
				{editMode ? (
					<Table.DropdownColumn
						header={formatMessage({id: 'deliverable.columns.role'})}
						label={getRoleName}
						state={getDropdownState}
						width="20%"
					>
						{sortedRoles.map(role => (
							<Table.DropdownColumn.ListItem
								onClick={row => {
									role?.node?.id && onRoleSelect(role?.node?.id, row);
								}}
							>
								{role?.node?.name}
							</Table.DropdownColumn.ListItem>
						))}
					</Table.DropdownColumn>
				) : (
					<Table.TextColumn
						accessorKey={'roleName'}
						header={formatMessage({id: 'deliverable.columns.role'})}
						ellipsis
					/>
				)}
				{editMode ? (
					<Table.NumberInputColumn
						accessorKey={'hours'}
						header={formatMessage({id: 'deliverable.columns.hours'})}
						onInputChange={recalculate}
						width="20%"
					/>
				) : (
					<Table.TimeColumn
						accessorKey={'hours'}
						header={formatMessage({id: 'deliverable.columns.hours'})}
						align={Table.Align.right}
						timeUnit="hours"
						visible={eyeOptionMap ? eyeOptionMap['time_estimates.hours'] : true}
					/>
				)}
				<Table.CurrencyColumn
					accessorKey={'valueOfService'}
					header={formatMessage({id: 'deliverable.columns.value_of_service'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={eyeOptionMap ? eyeOptionMap['time_estimates.value_of_service'] : true}
				/>
				<Table.CurrencyColumn
					accessorKey={'revenue'}
					header={formatMessage({id: 'deliverable.columns.revenue'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={eyeOptionMap ? eyeOptionMap['time_estimates.revenue'] : true}
				/>
				<Table.CurrencyColumn
					accessorKey={'cost'}
					header={formatMessage({id: 'deliverable.columns.cost'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={eyeOptionMap ? eyeOptionMap['time_estimates.cost'] : true}
				/>
				<Table.CurrencyColumn
					accessorKey={'profit'}
					header={formatMessage({id: 'deliverable.columns.profit'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={eyeOptionMap ? eyeOptionMap['time_estimates.profit'] : true}
				/>
				<Table.CurrencyColumn
					accessorKey={'ratePerHour'}
					header={formatMessage({id: 'deliverable.columns.rate_per_hour'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={eyeOptionMap ? eyeOptionMap['time_estimates.rate_per_hour'] : true}
				/>
				<Table.CurrencyColumn
					accessorKey={'costPerHour'}
					header={formatMessage({id: 'deliverable.columns.cost_per_hour'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={eyeOptionMap ? eyeOptionMap['time_estimates.cost_per_hour'] : true}
				/>
				<Table.ActionColumn header={'contextmenu'} hideHeader hidden={!editMode}>
					<Table.ActionColumn.Option data-cy={'delete-baseline-role'} onClick={onDeleteRow}>
						{formatMessage({id: 'deliverable.columns.delete_time_estimate'})}
					</Table.ActionColumn.Option>
				</Table.ActionColumn>
			</Table>
		</>
	);
});
